import { createFileRoute, Link } from "@tanstack/react-router";
import { usePageTitle } from "@/hooks/use-page-title";
import { useCheckoutStatus, subscriptionKeys } from "@/lib/api/subscription";
import { dashboardKeys } from "@/lib/api/dashboard";
import { PLANS } from "@/config/plans";
import type { PlanKey } from "@/lib/api/types";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  Crown,
  Loader2,
  XCircle,
  ArrowRight,
  RefreshCw,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { MotionConfig, motion } from "motion/react";

export const Route = createFileRoute("/dashboard/checkout/return")({
  component: CheckoutReturnPage,
  validateSearch: (search: Record<string, unknown>) => ({
    session_id: (search.session_id as string) || "",
    tenantId: (search.tenantId as string) || "",
    plan: (search.plan as string) || "",
  }),
});

function CheckoutReturnPage() {
  usePageTitle("Payment");
  const { session_id, tenantId, plan } = Route.useSearch();
  const queryClient = useQueryClient();
  const { data, isPending } = useCheckoutStatus(tenantId, session_id || null);

  const status = data?.data?.status;
  const planConfig = plan && plan in PLANS ? PLANS[plan as PlanKey] : null;
  const planName = planConfig?.name ?? "your plan";
  const highlights = planConfig?.highlights ?? [];

  useEffect(() => {
    if (status === "complete") {
      queryClient.invalidateQueries({
        queryKey: subscriptionKeys.detail(tenantId),
      });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    }
  }, [status, tenantId, queryClient]);

  if (isPending || (!status && session_id)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="size-7 animate-spin text-rose-500" />
          <div className="text-center">
            <p className="font-heading font-semibold">
              Confirming your payment
            </p>
            <p className="text-muted-foreground mt-1 text-pretty text-sm">
              This usually takes just a few seconds...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === "complete") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <MotionConfig reducedMotion="user">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="w-full max-w-md"
          >
            <Card className="overflow-hidden">
              <CardContent className="flex flex-col items-center p-8 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    delay: 0.15,
                    type: "spring",
                    stiffness: 200,
                    damping: 14,
                  }}
                  className="relative mb-6"
                >
                  <div className="flex size-16 items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-red-500 shadow-lg shadow-rose-500/20">
                    <Crown className="size-8 text-white" />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, ease: "easeOut" }}
                >
                  <p className="font-mono text-[10px] font-medium uppercase tracking-wider text-rose-500">
                    {planName} Activated
                  </p>
                  <h1 className="font-heading mt-1.5 text-balance text-2xl font-bold">
                    Welcome to {planName}
                  </h1>
                  <p className="text-muted-foreground mt-2 text-pretty text-sm">
                    Your subscription is active. Here's what you unlocked:
                  </p>
                </motion.div>

                <Separator className="my-6" />

                <motion.ul
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.45, ease: "easeOut" }}
                  className="w-full space-y-2"
                >
                  {highlights.map((label, i) => (
                    <motion.li
                      key={label}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        delay: 0.5 + i * 0.05,
                        ease: "easeOut",
                      }}
                      className="flex items-center gap-2.5 text-left"
                    >
                      <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-rose-500/10">
                        <Check className="size-3 text-rose-500" />
                      </div>
                      <span className="text-sm">{label}</span>
                    </motion.li>
                  ))}
                </motion.ul>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7, ease: "easeOut" }}
                  className="mt-8 w-full"
                >
                  <Button
                    asChild
                    className="w-full bg-gradient-to-r from-rose-500 to-red-500 text-white hover:from-rose-600 hover:to-red-600"
                  >
                    <Link
                      to="/dashboard/teams/$tenantId"
                      params={{ tenantId }}
                      search={{ tab: "subscription" }}
                    >
                      Go to Dashboard
                      <ArrowRight className="ml-2 size-4" />
                    </Link>
                  </Button>
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        </MotionConfig>
      </div>
    );
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <MotionConfig reducedMotion="user">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="w-full max-w-md"
        >
          <Card>
            <CardContent className="flex flex-col items-center p-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  delay: 0.15,
                  type: "spring",
                  stiffness: 200,
                  damping: 15,
                }}
                className="bg-destructive/10 mb-6 flex size-14 items-center justify-center rounded-full"
              >
                <XCircle className="text-destructive size-7" />
              </motion.div>

              <h1 className="font-heading mb-2 text-balance text-xl font-bold">
                Payment didn't go through
              </h1>
              <p className="text-muted-foreground text-pretty text-sm">
                Don't worry — you haven't been charged. This can happen if your
                card was declined, the session expired, or there was a temporary
                network issue.
              </p>

              <div className="mt-8 flex w-full flex-col gap-2">
                <Button
                  asChild
                  className="w-full bg-gradient-to-r from-rose-500 to-red-500 text-white hover:from-rose-600 hover:to-red-600"
                >
                  <Link to="/dashboard/checkout" search={{ tenantId, plan }}>
                    <RefreshCw className="mr-2 size-4" />
                    Try Again
                  </Link>
                </Button>
                <Button variant="ghost" size="sm" asChild>
                  <Link
                    to="/dashboard/teams/$tenantId"
                    params={{ tenantId }}
                    search={{ tab: "subscription" }}
                  >
                    Back to Subscription
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </MotionConfig>
    </div>
  );
}
